import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import EnhancedBlogEditor from "../admin/Components/EnhancedBlogEditor";

const API_HOST = process.env.REACT_APP_API_URL;

const EditBlog = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [title, setTitle] = useState("");
  const [creator, setCreator] = useState("");
  const [content, setContent] = useState(null);
  const [excerpt, setExcerpt] = useState("");
  const [homeImage, setHomeImage] = useState(null);
  const [currentImageUrl, setCurrentImageUrl] = useState("");
  const [tags, setTags] = useState([]);
  const [categories, setCategories] = useState([]);
  const [Hpriority, setHpriority] = useState(0);
  const [Bpriority, setBpriority] = useState(0);
  const [visibility, setVisibility] = useState("private");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchBlog();
  }, [id]);

  const fetchBlog = async () => {
    const authToken = sessionStorage.getItem("authToken");
    try {
      const response = await axios.get(`${API_HOST}blogs/by-id/${id}`, {
        headers: { "auth-token": authToken },
      });

      const blog = response.data;
      setTitle(blog.title);
      setCreator(blog.creator);
      setContent(blog.content);
      setExcerpt(blog.excerpt || "");
      setCurrentImageUrl(blog.homeImage);
      setTags(blog.tags || []);
      setCategories(blog.categories || []);
      setHpriority(blog.priority?.home || 0);
      setBpriority(blog.priority?.blog || 0);
      setVisibility(blog.visibility || "private");
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching blog:", error);
      alert("Failed to load blog");
      navigate("/blogHead");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title || !creator || !content) {
      alert("Please fill all required fields");
      return;
    }

    setIsSubmitting(true);
    const authToken = sessionStorage.getItem("authToken");
    const formData = new FormData();
    formData.append("title", title);
    formData.append("creator", creator);
    formData.append("content", JSON.stringify(content));
    formData.append("excerpt", excerpt);
    if (homeImage) {
      formData.append("homeImage", homeImage);
    }
    formData.append("tags", JSON.stringify(tags));
    formData.append("categories", JSON.stringify(categories));
    formData.append("Hpriority", Hpriority);
    formData.append("Bpriority", Bpriority);
    formData.append("visibility", visibility);

    try {
      const response = await axios.put(`${API_HOST}blogs/editBlog/${id}`, formData, {
        headers: {
          "auth-token": authToken,
        },
      });

      if (response.status === 200) {
        alert("Blog updated successfully!");
        navigate("/blogHead");
      }
    } catch (error) {
      console.error("Error updating blog:", error);
      alert("Failed to update blog. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="container" style={{ padding: "40px 20px", textAlign: "center" }}>
        <h2>Loading blog...</h2>
      </div>
    );
  }

  return (
    <div className="container" style={{ padding: "40px 20px", maxWidth: "1200px" }}>
      <div style={{ marginBottom: "30px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h1>Edit Blog</h1>
        <button onClick={() => navigate("/blogHead")} className="logout-button">
          Back to Blogs
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: "20px" }}>
          <label style={{ display: "block", marginBottom: "8px", fontWeight: "bold" }}>
            Title <span style={{ color: "red" }}>*</span>
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Enter blog title"
            style={{
              width: "100%",
              padding: "12px",
              fontSize: "16px",
              borderRadius: "4px",
              border: "1px solid #444",
              background: "#2a2a2a",
              color: "#fff"
            }}
            required
          />
        </div>

        <div style={{ marginBottom: "20px" }}>
          <label style={{ display: "block", marginBottom: "8px", fontWeight: "bold" }}>
            Creator Name
          </label>
          <input
            type="text"
            value={creator}
            readOnly
            style={{
              width: "100%",
              padding: "12px",
              fontSize: "16px",
              borderRadius: "4px",
              border: "1px solid #444",
              background: "#1a1a1a",
              color: "#888",
              cursor: "not-allowed"
            }}
            title="Original blog creator - cannot be changed"
          />
          <small style={{ color: "#888", fontSize: "12px" }}>Original blog creator (read-only)</small>
        </div>

        <div style={{ marginBottom: "20px" }}>
          <label style={{ display: "block", marginBottom: "8px", fontWeight: "bold" }}>
            Excerpt (SEO Description)
          </label>
          <textarea
            value={excerpt}
            onChange={(e) => setExcerpt(e.target.value)}
            placeholder="Enter a brief excerpt for SEO (max 200 characters)"
            maxLength={200}
            rows={3}
            style={{
              width: "100%",
              padding: "12px",
              fontSize: "16px",
              borderRadius: "4px",
              border: "1px solid #444",
              background: "#2a2a2a",
              color: "#fff"
            }}
          />
          <small>{excerpt.length}/200 characters</small>
        </div>

        <div style={{ marginBottom: "20px" }}>
          <label style={{ display: "block", marginBottom: "8px", fontWeight: "bold" }}>
            Content <span style={{ color: "red" }}>*</span>
          </label>
          <EnhancedBlogEditor onChange={setContent} initialData={content} />
        </div>

        <div style={{ marginBottom: "20px" }}>
          <label style={{ display: "block", marginBottom: "8px", fontWeight: "bold" }}>
            Home Image
          </label>
          {currentImageUrl && (
            <div style={{ marginBottom: "10px" }}>
              <img src={currentImageUrl} alt="Current" style={{ maxWidth: "300px", borderRadius: "8px" }} />
              <p style={{ fontSize: "14px", color: "#666" }}>Current image (upload new to replace)</p>
            </div>
          )}
          <input
            type="file"
            accept="image/*"
            onChange={(e) => setHomeImage(e.target.files[0])}
            style={{
              width: "100%",
              padding: "12px",
              fontSize: "16px"
            }}
          />
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px", marginBottom: "20px" }}>
          <div>
            <label style={{ display: "block", marginBottom: "8px", fontWeight: "bold" }}>Tags (comma-separated)</label>
            <input
              type="text"
              value={tags.join(", ")}
              onChange={(e) => setTags(e.target.value.split(",").map(t => t.trim()).filter(Boolean))}
              placeholder="e.g., web development, react, javascript"
              style={{
                width: "100%",
                padding: "12px",
                fontSize: "16px",
                borderRadius: "4px",
                border: "1px solid #444",
                background: "#2a2a2a",
                color: "#fff"
              }}
            />
          </div>

          <div>
            <label style={{ display: "block", marginBottom: "8px", fontWeight: "bold" }}>Categories (comma-separated)</label>
            <input
              type="text"
              value={categories.join(", ")}
              onChange={(e) => setCategories(e.target.value.split(",").map(c => c.trim()).filter(Boolean))}
              placeholder="e.g., technology, tutorials"
              style={{
                width: "100%",
                padding: "12px",
                fontSize: "16px",
                borderRadius: "4px",
                border: "1px solid #444",
                background: "#2a2a2a",
                color: "#fff"
              }}
            />
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "20px", marginBottom: "20px" }}>
          <div>
            <label style={{ display: "block", marginBottom: "8px", fontWeight: "bold" }}>Home Priority (0-10)</label>
            <input
              type="number"
              min="0"
              max="10"
              value={Hpriority}
              onChange={(e) => setHpriority(parseInt(e.target.value))}
              style={{
                width: "100%",
                padding: "12px",
                fontSize: "16px",
                borderRadius: "4px",
                border: "1px solid #444",
                background: "#2a2a2a",
                color: "#fff"
              }}
            />
          </div>

          <div>
            <label style={{ display: "block", marginBottom: "8px", fontWeight: "bold" }}>Blog Priority (0-10)</label>
            <input
              type="number"
              min="0"
              max="10"
              value={Bpriority}
              onChange={(e) => setBpriority(parseInt(e.target.value))}
              style={{
                width: "100%",
                padding: "12px",
                fontSize: "16px",
                borderRadius: "4px",
                border: "1px solid #444",
                background: "#2a2a2a",
                color: "#fff"
              }}
            />
          </div>

          <div>
            <label style={{ display: "block", marginBottom: "8px", fontWeight: "bold" }}>Visibility</label>
            <select
              value={visibility}
              onChange={(e) => setVisibility(e.target.value)}
              style={{
                width: "100%",
                padding: "12px",
                fontSize: "16px",
                borderRadius: "4px",
                border: "1px solid #444",
                background: "#2a2a2a",
                color: "#fff"
              }}
            >
              <option value="private">Private</option>
              <option value="public">Public</option>
            </select>
          </div>
        </div>

        <div style={{ display: "flex", gap: "10px", marginTop: "30px" }}>
          <button
            type="submit"
            disabled={isSubmitting}
            className="logout-button"
            style={{ flex: 1 }}
          >
            {isSubmitting ? "Updating..." : "Update Blog"}
          </button>
          <button
            type="button"
            onClick={() => navigate("/blogHead")}
            className="logout-button"
            style={{ flex: 1, background: "#6c757d" }}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditBlog;
